import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import CreateContact from "./CreateContact";
import type { Contact } from "../../types/deal";
import { apiGetById } from "../../services/genesisApi/httpsMethods";
import { Alert, CircularProgress, Box } from "@mui/material";

type LoadResult = {
  /** Route id this result belongs to; a different id means still loading. */
  id: string | undefined;
  contact: Partial<Contact> | null;
  error: string | null;
};

const EditContactWrapper = () => {
  const { contactId } = useParams();
  const [result, setResult] = useState<LoadResult | null>(null);
  // Never show the previous contact (or a blank Create form) while a new id loads
  const loading = !result || result.id !== contactId;

  useEffect(() => {
    let cancelled = false;

    const fetchContact = async () => {
      try {
        const response = await apiGetById("contact", Number(contactId)); // API call
        if (cancelled) return;
        if (response && response.success && response.data?.[0]) {
          setResult({ id: contactId, contact: response.data[0], error: null });
        } else {
          setResult({
            id: contactId,
            contact: null,
            error:
              !response?.success && response?.status !== 404
                ? "Failed to load the contact. Please try again."
                : "Contact not found.",
          });
        }
      } catch (error) {
        console.error("Failed to fetch contact", error);
        if (!cancelled) {
          setResult({
            id: contactId,
            contact: null,
            error: "Failed to load the contact. Please try again.",
          });
        }
      }
    };

    fetchContact();
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Without a loaded record the form would act as Create (and POST a new one)
  if (result.error || !result.contact) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{result.error || "Contact not found."}</Alert>
      </Box>
    );
  }

  return <CreateContact key={contactId} contact={result.contact} isEdit={true} />;
};

export default EditContactWrapper;
